import { useDraggable } from '@dnd-kit/core';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import { SwitchCase, Case, DefaultCase } from '@ui/switch-case/switch-case';
import { ICONS } from '@ui/icons/types';
import { WIDGET_DEFINITIONS, WidgetType, type WidgetDefinition } from '../../../templates/widgets/widget-registry';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setSelectedBlockId,
  updateBlockImage,
  updateBlockProp,
  updateBlockStyle,
} from '@/store/slices/campaign-editor';
import { selectSelectedBlock } from '@/store/slices/campaign-editor/selectors';
import { selectProjectStartTimestamp } from '@/store/slices/project/selectors';
import ContentBlocksTopSection from './content-blocks-top-section';
import BlockStylePanel from './block-style-panel';
import LogoStylePanel from './logo-style-panel';
import ImageStylePanel from './image-style-panel';
import SpacerStylePanel from './spacer-style-panel';
import DividerStylePanel from './divider-style-panel';
import ButtonStylePanel from './button-style-panel';
import SocialStylePanel from './social-style-panel';
import VideoStylePanel from './video-style-panel';
import FooterStylePanel from './footer-style-panel';
import DateTimeStylePanel from './date-time-style-panel';

interface ContentBlocksPanelProps {
  onClose: () => void;
  isEditMode: boolean;
  backdropColor: string;
  pageColor: string;
  timezone?: string;
}

function WidgetTileIcon({ widget }: { widget: WidgetDefinition }) {
  return (
    <Container
      className="flex ai-center jc-center"
      background="var(--gray-800)"
      width="32px"
      height="32px"
      borderRadius="8px"
    >
      <Icon name={widget.icon} size={20} color="var(--global-ico-text-2)" />
    </Container>
  );
}

export function WidgetTilePreview({ widget }: { widget: WidgetDefinition }) {
  return (
    <Container
      className="flex-column ai-center jc-center"
      background="var(--global-cont-2)"
      borderRadius="12px"
      width="97px"
      height="97px"
    >
      <WidgetTileIcon widget={widget} />
      <Text className="L2" color="var(--global-ico-text-1)">
        {widget.label}
      </Text>
    </Container>
  );
}

interface DraggableWidgetTileProps {
  widget: WidgetDefinition;
  isEditMode: boolean;
}

function DraggableWidgetTile({ widget, isEditMode }: DraggableWidgetTileProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: widget.type,
    data: { widgetType: widget.type },
    disabled: !isEditMode,
  });

  return (
    <Container
      ref={setNodeRef}
      className="flex-column ai-center jc-center gap-8 cursor-grab"
      background="var(--global-cont-2)"
      hoverBackground="var(--gray-800)"
      border="1px solid transparent"
      hoverBorder="1px solid var(--gray-700)"
      borderRadius="12px"
      width="97px"
      height="97px"
      opacity={isDragging ? 0.4 : 1}
      {...attributes}
      {...listeners}
    >
      <WidgetTileIcon widget={widget} />
      <Text className="L2" color="var(--global-ico-text-1)">
        {widget.label}
      </Text>
    </Container>
  );
}

export default function ContentBlocksPanel({
  isEditMode,
  backdropColor,
  pageColor,
  timezone,
}: ContentBlocksPanelProps) {
  const dispatch = useAppDispatch();
  const selectedBlock = useAppSelector(selectSelectedBlock);
  // the project's start, as set on the overview page's date & time
  const startTimestamp = useAppSelector(selectProjectStartTimestamp);
  const onUpdateBlockStyle = (blockId: string, key: string, value: unknown) =>
    dispatch(updateBlockStyle({ blockId, key, value }));
  const onUpdateBlockProp = (blockId: string, key: string, value: unknown) =>
    dispatch(updateBlockProp({ blockId, key, value }));
  const onUpdateBlockImage = (blockId: string, src: string) => dispatch(updateBlockImage({ blockId, src }));
  const selectedWidgetDefinition = selectedBlock
    ? WIDGET_DEFINITIONS.find((widget) => widget.type === selectedBlock.type)
    : undefined;

  return (
    <>
      <Container className="flex ai-center jc-space-between padding-20">
        <Container className="flex ai-center gap-8">
          {selectedBlock ? (
            <Button
              clear
              width="24px"
              padding="0"
              borderRadius="6px"
              hoverBackgroundColor="var(--gray-900)"
              onClick={() => dispatch(setSelectedBlockId(null))}
            >
              <Icon name={ICONS.ARROW_LEFT} size={16} color="var(--global-ico-text-1)" />
            </Button>
          ) : null}
          <Text className="H5" color="var(--global-ico-text-1)">
            {selectedBlock ? (selectedWidgetDefinition?.label ?? selectedBlock.type) : 'Content Blocks'}
          </Text>
        </Container>
      </Container>
      <Container className="flex-column flex-1 min-height-0 overflow-y-auto overflow-x-hidden padding-v-8 gap-8">
        {selectedBlock ? (
          <SwitchCase input={selectedBlock.type}>
            <Case value={[WidgetType.Header, WidgetType.Paragraph]}>
              <BlockStylePanel
                block={selectedBlock}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
              />
            </Case>
            <Case value={WidgetType.DateTimeCard}>
              <DateTimeStylePanel
                block={selectedBlock}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
                onUpdateProp={(key, value) => onUpdateBlockProp(selectedBlock.id, key, value)}
                startTimestamp={startTimestamp}
                timezone={timezone}
              />
            </Case>
            <Case value={WidgetType.Logo}>
              <LogoStylePanel
                block={selectedBlock}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
                onUpdateSrc={(src) => onUpdateBlockImage(selectedBlock.id, src)}
              />
            </Case>
            <Case value={WidgetType.Image}>
              <ImageStylePanel
                block={selectedBlock}
                onUpdateSrc={(src) => onUpdateBlockImage(selectedBlock.id, src)}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
                onUpdateProp={(key, value) => onUpdateBlockProp(selectedBlock.id, key, value)}
              />
            </Case>
            <Case value={WidgetType.Video}>
              <VideoStylePanel
                block={selectedBlock}
                onUpdateSrc={(src) => onUpdateBlockImage(selectedBlock.id, src)}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
                onUpdateProp={(key, value) => onUpdateBlockProp(selectedBlock.id, key, value)}
              />
            </Case>
            <Case value={WidgetType.Spacer}>
              <SpacerStylePanel
                block={selectedBlock}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
                onUpdateProp={(key, value) => onUpdateBlockProp(selectedBlock.id, key, value)}
              />
            </Case>
            <Case value={WidgetType.Divider}>
              <DividerStylePanel
                block={selectedBlock}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
              />
            </Case>
            <Case value={WidgetType.Button}>
              <ButtonStylePanel
                block={selectedBlock}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
                onUpdateProp={(key, value) => onUpdateBlockProp(selectedBlock.id, key, value)}
              />
            </Case>
            <Case value={WidgetType.Social}>
              <SocialStylePanel
                block={selectedBlock}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
                onUpdateProp={(key, value) => onUpdateBlockProp(selectedBlock.id, key, value)}
              />
            </Case>
            <Case value={WidgetType.Footer}>
              {/* keyed so its typed-but-not-yet-applied text can't carry over to another footer */}
              <FooterStylePanel
                key={selectedBlock.id}
                block={selectedBlock}
                onUpdateSrc={(src) => onUpdateBlockImage(selectedBlock.id, src)}
                onUpdateStyle={(key, value) => onUpdateBlockStyle(selectedBlock.id, key, value)}
                onUpdateProp={(key, value) => onUpdateBlockProp(selectedBlock.id, key, value)}
              />
            </Case>
            <DefaultCase>
              <Container className="flex-column ai-center gap-16" padding="24px 16px" style={{ textAlign: 'center' }}>
                <Text className="B2" color="var(--global-ico-text-3)">
                  {selectedWidgetDefinition?.label ?? selectedBlock.type} configuration is coming soon.
                </Text>
              </Container>
            </DefaultCase>
          </SwitchCase>
        ) : (
          <>
            <ContentBlocksTopSection backdropColor={backdropColor} pageColor={pageColor} />
            <Container
              className="grid"
              gridTemplateColumns="repeat(3, 97px)"
              gap="8px"
              padding="0 16px"
              style={{ justifyContent: 'center' }}
            >
              {WIDGET_DEFINITIONS.map((widget) => (
                <DraggableWidgetTile key={widget.type} widget={widget} isEditMode={isEditMode} />
              ))}
            </Container>
          </>
        )}
      </Container>
    </>
  );
}
